/**
 * Deleting Card Screen
 *
 * Loading state shown while deleting a saved card via API.
 * Displays spinner and "Deleting [cardname]" text.
 */

import type { FC } from 'react';
import styles from './DeletingCard.module.css';

interface DeletingCardProps {
  /**
   * Name of the card being deleted (e.g., "Visa 4456")
   */
  cardName: string;
}

/**
 * Deleting Card Screen Component
 *
 * Shows during DELETE API call to provide visual feedback.
 * Matches container size (570px × 656px) of other screens.
 *
 * @example
 * <DeletingCard cardName="Visa 4456" />
 */
export const DeletingCard: FC<DeletingCardProps> = ({ cardName }) => {
  return (
    <div className={styles.container} role="status" aria-live="polite" aria-busy="true">
      {/* Spinner SVG */}
      <div className={styles.spinner} aria-label="Loading">
        <svg
          width="120"
          height="120"
          viewBox="0 0 120 120"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className={styles.spinnerSvg}
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M60 110C87.6142 110 110 87.6142 110 60C110 32.3858 87.6142 10 60 10C32.3858 10 10 32.3858 10 60C10 87.6142 32.3858 110 60 110ZM60 120C93.1371 120 120 93.1371 120 60C120 26.8629 93.1371 0 60 0C26.8629 0 0 26.8629 0 60C0 93.1371 26.8629 120 60 120Z"
            fill="url(#paint0_linear_deleting)"
          />
          <defs>
            <linearGradient
              id="paint0_linear_deleting"
              x1="60"
              y1="-3.25718e-06"
              x2="60"
              y2="120"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#06A4F3" />
              <stop offset="1" stopColor="white" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      {/* Text */}
      <p className={styles.text}>Deleting {cardName}</p>
    </div>
  );
};
